// @flow

import React from "react";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { push } from "connected-react-router";

import moment from "moment";
import { SectionComponent, Icons, ChartJS, GSGrid, Loader, StatItem, View } from "@components";

import type { State } from "@store/reducers";
import type { ValuesItemValue, TargetedReasons } from "@modules/reporting/reducer/types";
import * as reportingSelectors from "@modules/reporting/selectors";
import actionCreators from "@modules/reporting/actionCreator";
import { CoronaAbsencesFilter } from "@modules/reporting/components";
import { defaultReports } from "@modules/reporting/selectors/reports/absence-details";
import type { DefaultReports } from "@modules/reporting/reducer/reports/absence-details/types";
import { isFirstCareUser, isClientAdmin } from "@modules/account/selectors";
import { route } from "@routes/helper";

type PropsFromState = $Exact<{
  notificationReviews: ValuesItemValue[],
  targetedReasons: TargetedReasons | null,
  defaultReports: Array<DefaultReports>,
  isFirstCareUser: boolean,
  isClientAdmin: boolean,
  isLoading: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getToolsDashboard: typeof actionCreators.GetToolsDashboard,
  getTargetedReasons: typeof actionCreators.GetCoronavirusToolsDashboard,
  getAbsenceDetailsCharts: typeof actionCreators.GetAbsenceDetailsCharts,
  getAbsenceDetailsReport: typeof actionCreators.GetAbsenceDetailsReport,
  getAbsenceDefaultReports: typeof actionCreators.GetAbsenceDefaultReports,
  push: typeof push,
}>;

type Props = {
  clientId: number,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
};

class DashboardCoronavirusSection extends React.Component<Props> {
  get targetedReasons() {
    return this.props.targetedReasons;
  }

  get coronaRow1() {
    if (!this.targetedReasons) return <div />;
    const { byReason, statistics } = this.targetedReasons;
    if (!byReason || !statistics) return <div />;
    let finalItemCollection = [];
    finalItemCollection.push(byReason.find(item => item.key.indexOf("CONFIRMED") > 0));
    finalItemCollection.push(byReason.find(item => item.key.indexOf("SUSPECTED") > 0));
    finalItemCollection.push(byReason.find(item => item.key.indexOf("SELF") > 0));
    finalItemCollection.push(byReason.find(item => item.key.indexOf("DEPENDANT") > 0));
    finalItemCollection.push(statistics.filter(item => item.key === "total_absences")[0]);

    return finalItemCollection.length > 1
      ? finalItemCollection.map(item => {
          return (
            <View flex="1" minWidth="16vw" key={item.key}>
              <StatItem
                itemClassName={
                  "first-row-flex-10 corona-stat-item-container dashboard-overview-stat-item-wrap stat-item " +
                  item.key
                }
                title={this.props.t(item.key.toUpperCase())}
                // icon={
                //   <div className={"today-stat-item corona-stat-item " + item.key.toLowerCase()}>
                //     <Icons.EmptyIcon />
                //   </div>
                // }
                prefix={item.type === "PERCENTAGE" ? "%" : ""}
                value={item.value}
              />
            </View>
          );
        })
      : null;
  }

  get coronaRow2() {
    if (!this.targetedReasons) return <div />;
    const { statistics, byDepartment } = this.targetedReasons;

    let byGender = this.targetedReasons.byGender.filter(item => {
      return item.key !== "OTHER";
    });
    byGender = byGender.map(item => {
      return { ...item, key: item.key + "_ABSENCES" };
    });

    let finalItemCollection = [];

    finalItemCollection.push(statistics.filter(item => item.key === "average_days_lost")[0]);
    finalItemCollection.push(...byGender);

    const top_department = byDepartment.filter(item => item.key === "top_department");
    if (top_department.length) {
      const topDepartmentOverwites = {
        ...top_department[0],
        description: this.props.t("TOP_DEPARTMENT"),
        value: `${top_department[0].description} (${top_department[0].value} ${this.props.t(
          parseInt(top_department[0].value) > 1 ? "ABSENCES" : "ABSENCE",
        )})`,
        type: "STRING",
      };
      finalItemCollection.push(topDepartmentOverwites);
    } else {
      // no data ...
      finalItemCollection.push({
        key: "top_department",
        description: this.props.t("TOP_DEPARTMENT"),
        value: "N/A",
        type: "STRING",
      });
    }

    return finalItemCollection.length > 1
      ? finalItemCollection.map((item: ValuesItemValue) => {
          if (item && item.key) {
            return (
              <StatItem
                itemClassName={
                  "first-row-flex-10 corona-stat-item-container dashboard-overview-stat-item-wrap stat-item " +
                  item.key.toLowerCase()
                }
                key={item.key}
                title={this.props.t(item.key.toUpperCase())}
                value={
                  <div key={item.key} className={"item-value " + item.key}>
                    {item.value}
                    <span className="item-prefix">{item.type === "PERCENTAGE" ? "%" : ""}</span>
                  </div>
                }
              />
            );
          }
          return null;
        })
      : null;
  }

  getChartValue = (dataArray: ValuesItemValue[], key: string): string | number => {
    return (dataArray.find(el => el.key === key) || { value: "n/a" }).value;
  };

  submitForm = (filters: Array<string>) => {
    this.props.getTargetedReasons({ clientId: this.props.clientId, filters });
  };

  get totalCoronaAbsencesPerDayLast30DaysChart() {
    if (!this.targetedReasons || !this.targetedReasons.byDate) return <div />;
    const data = (this.targetedReasons || {}).byDate || [];

    return (
      <ChartJS
        type="bar"
        title={this.props.t("CORONAVIRUS_ABSENCES_PER_DAY_LAST_30_DAYS")}
        data={{
          labels: data.map(i => moment(i.description).format("DD/MM")),
          datasets: (data[0].value || []).map((series, idx) => ({
            label: series.description,
            data: data.map(row => row.value[idx].value),
          })),
        }}
        options={{ height: 200, isStacked: true, wrapXAxisLabels: true }}
      />
    );
  }

  get coronavirusDepartmentHeatMapLast30DaysGrid() {
    if (!this.targetedReasons) return <div />;
    let rowData = [];
    let maxValue = 0;
    let maxTotal = 0;
    rowData =
      this.targetedReasons &&
      this.targetedReasons.byDateAndDepartment[0].value.map((row: Object) => {
        return {
          department: row.description,
          total: 0,
        };
      });

    let columnDefs =
      (this.targetedReasons &&
        this.targetedReasons.byDateAndDepartment.map((row: Object, idx: number) => {
          const key = row.key;

          row.value.map((v: Object, i: number) => {
            // 1 ROW on each iteration
            const val = parseInt(v.value);
            rowData[i][key] = val;
            rowData[i].total += val;
            maxValue = maxValue > val ? maxValue : val;
            maxTotal = maxTotal > rowData[i].total ? maxTotal : rowData[i].total;
          });

          return {
            width: 30,
            key,
            cellClass: "text-center p-1px",
            renderCell: (params: Object) => {
              return GSGrid.render.heatMap({
                color: [212, 63, 58],
                value: params.row[params.column.key],
                maxValue,
              });
            },
            name: moment(row.description).format("DD/MM"),
          };
        })) ||
      [];

    columnDefs.unshift({
      name: this.props.t("DEPARTMENT"),
      key: "department",
      align: "left",
      frozen: "left",

      renderCell: (params: Object) => {
        return <span title={params.row[params.column.key]}>{params.row[params.column.key]}</span>;
      },
    });
    columnDefs.push({
      name: this.props.t("TOTAL"),
      key: "total",
      width: 100,
      cellClass: "p-1px",
      renderCell: (params: Object) => {
        return GSGrid.render.barIndicator({
          color: "rgba(255, 0, 0, 0.3)",
          total: params.row.total,
          max: maxTotal,
          gradient: false,
        });
      },
    });

    if (rowData.length === 1 && rowData[0]?.total === 0) {
      return null;
    }

    return <GSGrid columns={columnDefs} rows={rowData} height={600} />;
  }
  get absencesByAgeGroupChart() {
    if (!this.targetedReasons) return null;
    const byAgeGroup = this.targetedReasons.byAgeGroup || [];
    return byAgeGroup.length ? (
      <View className="gs-container" flex={1}>
        <ChartJS
          type="bar"
          title={this.props.t("CORONAVIRUS_ABSENCES_BY_AGE_GROUP")}
          data={{
            labels: byAgeGroup.map(item => this.props.t(item.key)),
            datasets: [
              {
                label: this.props.t("CORONAVIRUS_RELATED_ABSENCES_BY_AGE_GROUP"),
                data: byAgeGroup.map(item => item.value),
              },
            ],
          }}
          options={{ height: 200, wrapXAxisLabels: true }}
        />
      </View>
    ) : null;
  }

  navigateToOpenAbsenceInfectiousReport = () => {
    const openAbsencesInfectiousReportData = this.props.defaultReports.find(
      report => report.name === this.props.t("CORONAVIRUS_ABSENCES"),
    );

    if (openAbsencesInfectiousReportData) {
      this.props.getAbsenceDetailsCharts({
        clientId: this.props.clientId,
        filters: openAbsencesInfectiousReportData.filters,
      });
      this.props.getAbsenceDetailsReport({
        clientId: this.props.clientId,
        report: openAbsencesInfectiousReportData,
        pageable: {
          page: 1,
        },
        callBack: () =>
          this.props.push(
            route("REPORTING_REPORTS_ABSENCE_DETAILS_REPORT_PATH", {
              clientId: this.props.clientId,
            }),
          ),
      });
    }
  };

  render() {
    return (
      <>
        {this.props.isLoading && <Loader />}
        <View separator="bottom" justifyContent="space-between">
          <View direction="row" justifyContent="space-between" overflow="hidden" flexWrap="wrap">
            {this.coronaRow1}
          </View>

          <View direction="row" justifyContent="space-between" overflow="hidden" flexWrap="wrap">
            {this.coronaRow2}
          </View>
        </View>
        <View gap={6}>
          <View className="gs-container" gap={6}>
            {this.props.targetedReasons && (
              <CoronaAbsencesFilter
                reasons={(this.targetedReasons || {}).targetedReasons || []}
                onSubmit={this.submitForm}
              />
            )}
            <View className="gs-container" flex={1}>
              {this.totalCoronaAbsencesPerDayLast30DaysChart}
            </View>
          </View>
          {this.coronavirusDepartmentHeatMapLast30DaysGrid}
        </View>
        <SectionComponent>{this.absencesByAgeGroupChart}</SectionComponent>
      </>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  notificationReviews: reportingSelectors.getToolsDashboardNotificationReviews(state),
  targetedReasons: reportingSelectors.getTargetedReasons(state),
  defaultReports: defaultReports(state),
  isFirstCareUser: isFirstCareUser(state),
  isClientAdmin: isClientAdmin(state),
  isLoading: reportingSelectors.isLoading(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getTargetedReasons: actionCreators.GetCoronavirusToolsDashboard,
  getToolsDashboard: actionCreators.GetToolsDashboard,
  getAbsenceDetailsCharts: actionCreators.GetAbsenceDetailsCharts,
  getAbsenceDetailsReport: actionCreators.GetAbsenceDetailsReport,
  getAbsenceDefaultReports: actionCreators.GetAbsenceDefaultReports,
  push,
};

export default compose(
  localize(),

  connect(mapStateToProps, mapDispatchToProps),
)(DashboardCoronavirusSection);
